import React, { useState, useRef, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import AdminLayout from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";
import { Upload, FolderUp, FileArchive, ArrowLeft, ArrowRight, Check, AlertTriangle, X, FileText } from "lucide-react";

// ── Category detection preview ─────────────────────────────────────────────────
// Mirrors the server-side `_BULK_CATEGORY_MAP` so the wizard shows an
// accurate preview BEFORE upload. Keep this list in sync with the backend.
const CATEGORY_MAP = {
  renderings: "images", renders: "images", rendering: "images",
  exterior: "images", exteriors: "images", interior: "images", interiors: "images",
  videos: "videos", video: "videos", walkthroughs: "videos",
  "floor plans": "floor_plans", floorplans: "floor_plans", plans: "floor_plans",
  "floor plan": "floor_plans", foundation: "floor_plans",
  roof: "floor_plans", site: "floor_plans",
  elevations: "elevations", elevation: "elevations",
  sections: "sections", section: "sections",
  "other details": "other_details", otherdetails: "other_details",
  details: "other_details", detail: "other_details",
  electrical: "other_details", plumbing: "other_details", hvac: "other_details",
  pdf: "pdf", pdfs: "pdf", documents: "pdf",
};

const EXT_KIND = {
  jpg: "image", jpeg: "image", png: "image", webp: "image", gif: "image",
  mp4: "video", webm: "video", mov: "video",
  pdf: "pdf",
};

const CATEGORY_LABELS = {
  images: "Renderings",
  videos: "Videos",
  floor_plans: "Floor Plans",
  elevations: "Elevations",
  sections: "Sections",
  other_details: "Other Details",
  pdf: "PDF",
  __plan_json__: "plan.json",
};

const classify = (relPath) => {
  const parts = relPath.replace(/\\/g, "/").split("/").filter(Boolean);
  if (!parts.length) return { category: "skipped", filename: "" };
  const filename = parts[parts.length - 1];
  if (filename.toLowerCase() === "plan.json") return { category: "__plan_json__", filename };
  const ext = filename.includes(".") ? filename.split(".").pop().toLowerCase() : "";
  if (!EXT_KIND[ext]) return { category: "skipped", filename, reason: `Unsupported .${ext || "?"}` };
  for (let i = parts.length - 2; i >= 0; i--) {
    const seg = parts[i].trim().toLowerCase();
    if (CATEGORY_MAP[seg]) return { category: CATEGORY_MAP[seg], filename, kind: EXT_KIND[ext] };
  }
  return { category: "other_details", filename, kind: EXT_KIND[ext], unknown: true };
};

const humanSize = (n) => n < 1024 ? `${n} B` : n < 1024 * 1024 ? `${(n / 1024).toFixed(0)} KB` : `${(n / 1024 / 1024).toFixed(1)} MB`;

const AdminBulkImport = () => {
  const navigate = useNavigate();
  const [files, setFiles] = useState([]);          // {file, relPath}
  const [zipMode, setZipMode] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const folderRef = useRef(null);
  const zipRef = useRef(null);
  const dropRef = useRef(null);

  const onFolderPicked = (fileList) => {
    setResult(null);
    const arr = Array.from(fileList || []).map((f) => ({
      file: f,
      relPath: f.webkitRelativePath || f.name,
    }));
    if (!arr.length) return;
    setZipMode(false);
    setFiles(arr);
  };

  const onZipPicked = (fileList) => {
    setResult(null);
    const f = (fileList || [])[0];
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".zip")) {
      toast.error("Please select a .zip archive");
      return;
    }
    setZipMode(true);
    setFiles([{ file: f, relPath: f.name }]);
  };

  const onDrop = (e) => {
    e.preventDefault();
    dropRef.current?.classList.remove("border-accent");
    const dt = e.dataTransfer;
    if (!dt) return;
    // If a single .zip is dropped, treat as zip mode
    const asFiles = Array.from(dt.files || []);
    if (asFiles.length === 1 && asFiles[0].name.toLowerCase().endsWith(".zip")) {
      onZipPicked(asFiles);
      return;
    }
    // Otherwise reject — browsers don't support folder-drop without DataTransferItemList entries recursion.
    // Ask the user to use the folder picker (best UX, cross-browser safe).
    if (dt.items && dt.items.length && dt.items[0].webkitGetAsEntry) {
      const items = Array.from(dt.items).map((i) => i.webkitGetAsEntry()).filter(Boolean);
      if (items.some((it) => it.isDirectory)) {
        // Recursively read entries.
        const collected = [];
        const walk = (entry, path) => new Promise((resolve) => {
          if (entry.isFile) {
            entry.file((f) => {
              collected.push({ file: f, relPath: path + "/" + f.name });
              resolve();
            });
          } else {
            const reader = entry.createReader();
            reader.readEntries((ents) => {
              Promise.all(ents.map((e2) => walk(e2, path + "/" + entry.name))).then(resolve);
            });
          }
        });
        Promise.all(items.map((it) => walk(it, ""))).then(() => {
          setZipMode(false);
          setFiles(collected.map((c) => ({ ...c, relPath: c.relPath.replace(/^\/+/, "") })));
        });
        return;
      }
    }
    toast.info("Drag a ZIP file, or click 'Choose folder' for folder uploads.");
  };

  const onDragOver = (e) => { e.preventDefault(); dropRef.current?.classList.add("border-accent"); };
  const onDragLeave = () => dropRef.current?.classList.remove("border-accent");

  // Preview grouping — classify each file before we upload.
  const preview = useMemo(() => {
    if (zipMode || !files.length) return null;
    const groups = {};
    let totalBytes = 0;
    let planJson = false;
    const skipped = [];
    for (const f of files) {
      const cls = classify(f.relPath);
      totalBytes += f.file.size;
      if (cls.category === "skipped") { skipped.push({ ...cls, size: f.file.size }); continue; }
      if (cls.category === "__plan_json__") { planJson = true; continue; }
      if (!groups[cls.category]) groups[cls.category] = [];
      groups[cls.category].push({ filename: cls.filename, size: f.file.size, unknown: cls.unknown });
    }
    return { groups, totalBytes, planJson, skipped };
  }, [files, zipMode]);

  const submit = async () => {
    if (!files.length) return;
    setUploading(true);
    setResult(null);
    try {
      const fd = new FormData();
      if (zipMode) {
        fd.append("files", files[0].file);
        fd.append("paths", "[]");
      } else {
        for (const f of files) fd.append("files", f.file);
        fd.append("paths", JSON.stringify(files.map((f) => f.relPath)));
      }
      const { data } = await api.post("/admin/plans/bulk-import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 300000,
      });
      setResult(data);
      toast.success(`Draft plan created: ${data.project_id} — ${data.title}`);
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      toast.error("Bulk import failed", { description: detail, duration: 12000 });
    } finally {
      setUploading(false);
    }
  };

  const clearAll = () => { setFiles([]); setResult(null); setZipMode(false); };

  return (
    <AdminLayout title="Bulk Import Wizard">
      <div className="max-w-4xl space-y-6">
        <Link to="/admin/plans" className="inline-flex items-center text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground">
          <ArrowLeft size={12} className="mr-1" /> Back to plans
        </Link>

        <div className="border border-border bg-card p-8">
          <div className="overline text-accent">Step 1 — Choose a source</div>
          <h2 className="font-display text-3xl mt-2">Upload a project folder or ZIP</h2>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
            Category folders are detected automatically — <span className="font-mono">Renderings</span>, <span className="font-mono">Videos</span>, <span className="font-mono">Floor Plans</span>, <span className="font-mono">Elevations</span>, <span className="font-mono">Sections</span>, <span className="font-mono">Other Details</span>, <span className="font-mono">PDF</span>. Include an optional <span className="font-mono">plan.json</span> at the folder root to prefill metadata. The imported plan is saved as <span className="font-semibold">Draft</span> for your review.
          </p>

          {/* Drop / picker area */}
          <div
            ref={dropRef}
            onDrop={onDrop}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            data-testid="bulk-drop-zone"
            className="mt-6 border-2 border-dashed border-border p-10 text-center transition"
          >
            <FolderUp size={36} className="mx-auto text-accent" />
            <div className="mt-4 font-semibold">Drag a ZIP here, or pick an option below</div>
            <div className="text-xs text-muted-foreground mt-1">Max total upload: 500 MB. Images / MP4 / PDF only.</div>
            <input
              ref={folderRef}
              type="file"
              webkitdirectory=""
              directory=""
              multiple
              className="hidden"
              onChange={(e) => onFolderPicked(e.target.files)}
              data-testid="bulk-folder-input"
            />
            <input
              ref={zipRef}
              type="file"
              accept=".zip"
              className="hidden"
              onChange={(e) => onZipPicked(e.target.files)}
              data-testid="bulk-zip-input"
            />
            <div className="mt-4 flex gap-3 justify-center flex-wrap">
              <Button type="button" onClick={() => folderRef.current?.click()} className="rounded-none bg-primary" data-testid="bulk-choose-folder-btn">
                <FolderUp size={14} className="mr-2" /> Choose folder
              </Button>
              <Button type="button" onClick={() => zipRef.current?.click()} variant="outline" className="rounded-none" data-testid="bulk-choose-zip-btn">
                <FileArchive size={14} className="mr-2" /> Choose ZIP
              </Button>
              {files.length > 0 && (
                <Button type="button" variant="ghost" onClick={clearAll} className="rounded-none text-rose-700" data-testid="bulk-clear-btn">
                  <X size={14} className="mr-1" /> Clear
                </Button>
              )}
            </div>
          </div>

          {/* Preview */}
          {zipMode && files[0] && (
            <div className="mt-6 border border-border bg-neutral-50 p-4" data-testid="bulk-zip-preview">
              <div className="overline text-accent">ZIP selected</div>
              <div className="font-semibold mt-1">{files[0].file.name}</div>
              <div className="text-xs text-muted-foreground">{humanSize(files[0].file.size)} — server will unpack & classify on upload.</div>
            </div>
          )}

          {preview && (
            <div className="mt-6" data-testid="bulk-folder-preview">
              <div className="overline text-accent">Detected structure</div>
              <div className="text-xs text-muted-foreground mt-1 mb-3">
                {files.length} files · {humanSize(preview.totalBytes)}
                {preview.planJson && <span className="ml-2 inline-flex items-center gap-1 text-emerald-700 font-semibold"><Check size={12} /> plan.json will fill metadata</span>}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {Object.entries(preview.groups).map(([cat, items]) => (
                  <div key={cat} className="border border-border p-3" data-testid={`bulk-preview-group-${cat}`}>
                    <div className="text-xs font-mono uppercase tracking-widest text-accent">{CATEGORY_LABELS[cat] || cat}</div>
                    <div className="text-2xl font-display mt-1">{items.length}</div>
                    <div className="text-[10px] text-muted-foreground mt-2 space-y-0.5 max-h-24 overflow-y-auto pr-1">
                      {items.slice(0, 8).map((it, i) => (
                        <div key={i} className={`truncate ${it.unknown ? "text-amber-700" : ""}`} title={it.filename}>
                          {it.unknown ? "⚠ " : ""}{it.filename}
                        </div>
                      ))}
                      {items.length > 8 && <div className="italic">+{items.length - 8} more…</div>}
                    </div>
                  </div>
                ))}
              </div>
              {preview.skipped.length > 0 && (
                <div className="mt-3 border-l-2 border-amber-400 bg-amber-50 p-3 text-xs">
                  <div className="font-semibold flex items-center gap-1"><AlertTriangle size={12} /> {preview.skipped.length} file(s) will be skipped</div>
                  <div className="mt-1 space-y-0.5 max-h-24 overflow-y-auto">
                    {preview.skipped.slice(0, 10).map((s, i) => (
                      <div key={i} className="truncate">{s.filename} — {s.reason || "unsupported"}</div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Submit */}
          {files.length > 0 && !result && (
            <div className="mt-6 flex justify-end">
              <Button
                onClick={submit}
                disabled={uploading}
                className="rounded-none bg-accent hover:bg-accent/90 text-neutral-900 h-12 px-8"
                data-testid="bulk-import-submit"
              >
                {uploading ? "Uploading & processing…" : <>Import as Draft <ArrowRight size={14} className="ml-2" /></>}
              </Button>
            </div>
          )}
        </div>

        {/* Result */}
        {result && (
          <div className="border border-emerald-200 bg-emerald-50 p-6" data-testid="bulk-import-result">
            <div className="flex items-center gap-2 text-emerald-800">
              <Check size={18} />
              <div className="font-semibold">Draft plan created — {result.project_id}</div>
            </div>
            <div className="mt-3 text-sm">
              <div><span className="text-muted-foreground">Title:</span> {result.title}</div>
              <div className="mt-2 flex gap-4 flex-wrap text-xs font-mono">
                <span>Images: <b>{result.summary.images}</b></span>
                <span>Videos: <b>{result.summary.videos}</b></span>
                <span>Drawings: <b>{result.summary.drawings}</b></span>
                <span>PDF: <b>{result.summary.pdf ? "yes" : "no"}</b></span>
                <span>Skipped: <b>{result.summary.skipped}</b></span>
                <span>plan.json: <b>{result.used_plan_json ? "yes" : "no"}</b></span>
              </div>
              {result.summary.unknown_folders?.length > 0 && (
                <div className="mt-3 text-xs text-amber-700">
                  <AlertTriangle size={12} className="inline mr-1" />
                  Unknown folders routed to Other Details: {result.summary.unknown_folders.join(", ")}
                </div>
              )}
              {result.warnings?.length > 0 && (
                <div className="mt-3 text-xs text-amber-700 space-y-0.5">
                  <div className="font-semibold">Warnings:</div>
                  {result.warnings.map((w, i) => <div key={i}>· {w}</div>)}
                </div>
              )}
            </div>
            <div className="mt-4 flex gap-2">
              <Button
                onClick={() => navigate(`/admin/plans/${result.plan_id}/edit`)}
                className="rounded-none bg-accent hover:bg-accent/90 text-neutral-900"
                data-testid="bulk-open-plan-btn"
              >
                <FileText size={14} className="mr-2" /> Open plan editor
              </Button>
              <Button
                variant="outline"
                className="rounded-none"
                onClick={clearAll}
                data-testid="bulk-import-another-btn"
              >
                <Upload size={14} className="mr-2" /> Import another
              </Button>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminBulkImport;
